import styles from './index.module.css'
import cn from 'clsx'
import startCase from 'lodash/startCase'
import React from 'react'
import { SegmentedControl } from 'components/ds'
type Props = Pick<
  React.ComponentProps<typeof SegmentedControl.Root>,
  'size' | 'variant' | 'radius' | 'className'
> & {
  value: boolean
  onChange?: (value: boolean) => any
  labelLeft: string | null | undefined
  labelRight: string | null | undefined
}

/**
 * A switch button group component that displays two toggle options as a segmented control
 * @param value - The current boolean value determining which option is selected
 * @param size - The size of the segmented control (defaults to '2')
 * @param variant - The visual variant of the control (defaults to 'classic')
 * @param radius - The border radius of the control (defaults to 'large')
 * @param onChange - Callback function called when the value changes
 * @param labelLeft - The text label for the left/first option
 * @param labelRight - The text label for the right/second option
 * @returns A segmented control component with two toggle options
 */
// SLAM-1943: Defaults moved to params because React 19 ignores defaultProps on function components.
const SwitchButtonGroup = ({
  value,
  size = '2',
  variant = 'classic',
  radius = 'large',
  className,
  onChange,
  labelLeft,
  labelRight,
}: Props) => {
  const handleChange = () => {
    if (onChange) {
      onChange(!value)
    }
  }

  const leftValue = labelLeft ?? ''
  const rightValue = labelRight ?? ''

  return (
    <SegmentedControl.Root
      defaultValue={value ? leftValue : rightValue}
      value={value ? leftValue : rightValue}
      onValueChange={handleChange}
      size={size}
      variant={variant}
      radius={radius}
      className={cn(styles['rt-SegmentedControlRoot'], className)}
    >
      <SegmentedControl.Item
        value={leftValue}
        className={cn(styles['rt-SegmentedControlItemLabel'], {
          [styles['rt-SegmentedControlItemLabelInactive']]: !value,
        })}
      >
        {startCase(leftValue)}
      </SegmentedControl.Item>
      <SegmentedControl.Item
        value={rightValue}
        className={cn(styles['rt-SegmentedControlItemLabel'], {
          [styles['rt-SegmentedControlItemLabelInactive']]: value,
        })}
      >
        {startCase(rightValue)}
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  )
}

export default SwitchButtonGroup
